import { useCallback, useContext, useEffect, useState } from 'react';
import EntityDetailPopup from '../../../utility/EntityDetailPopup';
import { DownArrowIcon, EyeIcon, cropClasses, miscClasses, popupClasses } from '../../../utility';
import { IconButton, InputAdornment, MenuItem, TextField, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import CropRunFRTLPopup from '../../../utility/popups/CropRunFRTLPopup';
import { getFrtlAliasString } from '../../../../utility/frtlUtils';
import { FRTLContext } from '../../../../context/FRTLContext';

export default function CreateSourcePackagePopup(props) {
  const { open, onClose, currDarkThemeClass, parentValues, handleAddPackage, isEditMode, currentPackage, bucketInstanceData = {} } = props;

  const [values, setValues] = useState({
    metrc_package_tag_number: '',
    frtl: '',
    metrc_unit_of_measurement: '',
    patient_license_number: '',
    package_note: '',
    package_date: new Date().toISOString().split('T')[0],
    current_package_weight: '',
    original_package_weight: '',
    item: '',
    harvests: [],
    buckets: [],
    selectedItem: '',
    selectedHarvest: '',
    selectedBuckets: {},
    frtlOpen: false,
  });

  const [metrcError, setMetrcError] = useState(false);
  const { frtlContextValue } = useContext(FRTLContext);

  useEffect(() => {
    if (isEditMode && currentPackage) {
      const selectedItem = bucketInstanceData?.items?.find(item => item.id === currentPackage.item) ?? {};

      const selectedBuckets = {};
      currentPackage.buckets?.forEach(bucketId => {
        selectedBuckets[bucketId] = true;
      });

      setValues({
        ...currentPackage,
        package_date: currentPackage.package_date || new Date().toISOString().split('T')[0],
        selectedItem: selectedItem?.id || '',
        selectedHarvest: currentPackage.harvests?.[0] || '',
        selectedBuckets,
        frtlOpen: false,
      });
    } else {
      setValues({
        metrc_package_tag_number: '',
        frtl: '',
        metrc_unit_of_measurement: '',
        patient_license_number: '',
        package_note: '',
        package_date: new Date().toISOString().split('T')[0],
        current_package_weight: '',
        original_package_weight: '',
        item: '',
        harvests: [],
        buckets: [],
        selectedItem: '',
        selectedHarvest: '',
        selectedBuckets: {},
        frtlOpen: false,
      });
    }
  }, [isEditMode, currentPackage, bucketInstanceData]);

  const handleSetValues = useCallback((prop, value) => setValues(state => ({ ...state, [prop]: value })), []);

  const handleMetrcChange = value => {
    if (value.length === 24) {
      setMetrcError(false);
    } else {
      setMetrcError(true);
    }
    handleSetValues('metrc_package_tag_number', value);
  };

  const handleItemChange = itemId => {
    const selectedItem = bucketInstanceData?.items?.find(item => item.id === itemId) ?? {};
    handleSetValues('selectedItem', itemId);
    handleSetValues('item', itemId);
    handleSetValues('metrc_unit_of_measurement', selectedItem?.metrc_unit_of_measurement || '');
  };

  const handleHarvestChange = harvestId => {
    handleSetValues('selectedHarvest', harvestId);
    handleSetValues('harvests', harvestId ? [harvestId] : []);
  };

  const handleBucketCheckboxChange = (bucketId, checked) => {
    setValues(prevState => {
      const newSelectedBuckets = { ...prevState.selectedBuckets };
      if (checked) {
        newSelectedBuckets[bucketId] = true;
      } else {
        delete newSelectedBuckets[bucketId];
      }

      const bucketsArray = Object.keys(newSelectedBuckets).map(id => parseInt(id));

      return {
        ...prevState,
        selectedBuckets: newSelectedBuckets,
        buckets: bucketsArray,
      };
    });
  };

  const handleSavePackage = () => {
    const payload = {
      ...values,
      harvests: values.selectedHarvest ? [values.selectedHarvest] : [],
      buckets: Object.keys(values.selectedBuckets).map(id => parseInt(id)),
    };
    delete payload.selectedHarvest;
    delete payload.selectedBuckets;
    delete payload.selectedItem;
    delete payload.frtlOpen;

    handleAddPackage(payload);
  };

  const eDPopupProps = {
    open,
    onClose,
    currDarkThemeClass,
    title: isEditMode ? values?.metrc_package_tag_number : 'Create New Source Package',
    headContentText: isEditMode ? 'Edit the source package details.' : 'Create a new source package with Metrc Package Tag Number.',
    onSaveClick: handleSavePackage,
  };

  const cropPlanMenuItemProps = {
    className: `${cropClasses?.menuItem} ${currDarkThemeClass}`,
    focusVisibleClassName: miscClasses?.menuItemFocusDark,
  };

  const textFieldProp = ({ prop, inputProps = {}, otherTextProps = {} }) => ({
    autoComplete: 'off',
    className: `${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`,
    fullWidth: true,
    inputProps,
    label: '',
    onChange: ({ target: { value } }) => handleSetValues(prop, value),
    value: values?.[prop] ?? '',
    ...otherTextProps,
  });

  const itemsList =
    bucketInstanceData?.items?.map((item, i) => (
      <MenuItem key={i} value={item?.id} {...cropPlanMenuItemProps}>
        {item?.item_name}
      </MenuItem>
    )) ?? [];

  if (!itemsList?.length) {
    itemsList?.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Items
      </MenuItem>
    );
  }

  const unitsList =
    bucketInstanceData?.unit?.map((unit, i) => (
      <MenuItem key={i} value={unit?.id} {...cropPlanMenuItemProps}>
        {unit?.unit_name}
      </MenuItem>
    )) ?? [];

  if (!unitsList?.length) {
    unitsList?.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Units
      </MenuItem>
    );
  }

  const harvestsList =
    bucketInstanceData?.harvests?.map((harvest, i) => (
      <MenuItem key={i} value={harvest?.id} {...cropPlanMenuItemProps}>
        {harvest?.harvest_name}
      </MenuItem>
    )) ?? [];

  if (!harvestsList?.length) {
    harvestsList?.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Harvests
      </MenuItem>
    );
  }

  const cppItemProps = {
    prop: 'selectedItem',
    otherTextProps: {
      label: 'Item',
      required: true,
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
      onChange: ({ target: { value } }) => handleItemChange(value),
    },
  };

  const cppUnitProps = {
    prop: 'metrc_unit_of_measurement',
    otherTextProps: {
      label: 'Unit of Measurement',
      required: true,
      select: true,
      disabled: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
    },
  };

  const cppHarvestProps = {
    prop: 'selectedHarvest',
    otherTextProps: {
      disabled: isEditMode,
      label: 'Harvest',
      required: true,
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
      onChange: ({ target: { value } }) => handleHarvestChange(value),
    },
  };

  const cppMetrcTagProps = {
    prop: 'metrc_package_tag_number',
    otherTextProps: {
      label: 'Metrc Package Tag Number',
      required: true,
      disabled: isEditMode,
      error: metrcError,
      helperText: metrcError ? 'Metrc Package Tag Number must have exactly 24 characters' : null,
    },
  };

  const cppPatientLicenseProps = {
    prop: 'patient_license_number',
    otherTextProps: {
      label: 'Patient License Number',
      required: true,
    },
  };

  const cppPackageNoteProps = {
    prop: 'package_note',
    otherTextProps: {
      label: 'Package Note',
      multiline: true,
      rows: 3,
    },
  };

  const cppPackageDateProps = {
    prop: 'package_date',
    otherTextProps: {
      label: 'Package Date',
      type: 'date',
      required: true,
      InputLabelProps: {
        shrink: true,
      },
    },
  };

  const cppCurrentPackageWeightProps = {
    prop: 'current_package_weight',
    otherTextProps: {
      label: 'Current Package Weight',
      type: 'number',
      required: true,
      // inputProps: { min: 0, step: 0.01 },
    },
  };

  const cppOriginalPackageWeightProps = {
    prop: 'original_package_weight',
    otherTextProps: {
      disabled: isEditMode,
      label: 'Original Package Weight',
      type: 'number',
      required: true,
      // inputProps: { min: 0, step: 0.01 },
    },
  };

  const handleOpenFRTLPopup = () => {
    handleSetValues('frtlOpen', true);
  };

  const handleFRTLClose = () => {
    handleSetValues('frtlOpen', false);
  };

  const cppFrtlProps = {
    otherTextProps: {
      label: 'FRTL (Farm - Room - Table - Level)',
      value: getFrtlAliasString(values?.frtl, frtlContextValue) ?? '',
      placeholder: 'FARM - ROOM - TABLE - LEVEL',
      required: true,
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={handleOpenFRTLPopup}>
              <EyeIcon />
            </IconButton>
          </InputAdornment>
        ),
      },
      // disabled: isEditMode,
    },
    inputProps: {
      disabled: true,
    },
  };

  const cropRunFRTLProps = {
    open: values?.frtlOpen,
    currDarkThemeClass,
    onClose: handleFRTLClose,
    handleEditFrtl: value => handleSetValues('frtl', value),
    selectedFarmId: parentValues?.farm,
    // metrcStatus: isEditMode ? 'metrc_validated' : null,
    farm: +values?.frtl?.split('.')[0] ?? '',
    room: +values?.frtl?.split('.')[1] ?? '',
    table: +values?.frtl?.split('.')[2] ?? '',
    level: +values?.frtl?.split('.')[3] ?? '',
  };

  return (
    <EntityDetailPopup {...eDPopupProps}>
      {!isEditMode && <TextField {...textFieldProp(cppMetrcTagProps)} onChange={e => handleMetrcChange(e.target.value)} />}
      <TextField {...textFieldProp(cppFrtlProps)} />
      <TextField {...textFieldProp(cppItemProps)}>{itemsList}</TextField>
      <TextField {...textFieldProp(cppUnitProps)}>{unitsList}</TextField>
      <TextField {...textFieldProp(cppHarvestProps)}>{harvestsList}</TextField>
      <TextField {...textFieldProp(cppPatientLicenseProps)} />
      <TextField {...textFieldProp(cppPackageDateProps)} />
      <TextField {...textFieldProp(cppOriginalPackageWeightProps)} />
      <TextField {...textFieldProp(cppCurrentPackageWeightProps)} />
      <TextField {...textFieldProp(cppPackageNoteProps)} />
      {bucketInstanceData?.buckets && bucketInstanceData.buckets.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>Select Buckets:</label>
          <FormGroup>
            {bucketInstanceData.buckets.map(bucket => (
              <FormControlLabel
                key={bucket.id}
                control={
                  <Checkbox
                    checked={values.selectedBuckets[bucket.id] || false}
                    onChange={e => handleBucketCheckboxChange(bucket.id, e.target.checked)}
                  />
                }
                label={bucket.name || `Bucket ${bucket.id}`}
              />
            ))}
          </FormGroup>
        </div>
      )}

      {values?.frtlOpen && <CropRunFRTLPopup {...cropRunFRTLProps} />}
    </EntityDetailPopup>
  );
}
